import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { ExternalLink, FileText } from "lucide-react";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";

interface MarketActivityItem {
  id: string;
  title: string;
  url?: string;
  description?: string;
  category?: string;
  item_type?: string;
  published_date?: string;
  thumbnail_url?: string;
  document_url?: string;
  ai_summary?: string;
}

interface MarketActivityWidgetProps {
  projectId: string;
}

export default function MarketActivityWidget({
  projectId,
}: MarketActivityWidgetProps) {
  const [items, setItems] = useState<MarketActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketActivity();
  }, [projectId]);

  const fetchMarketActivity = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("project_market_activity")
        .select(
          `
          market_activity (
            id,
            title,
            url,
            description,
            category,
            item_type,
            published_date,
            thumbnail_url,
            document_url,
            ai_summary,
            display_order
          )
        `
        )
        .eq("project_id", projectId)
        .order("market_activity(published_date)", { ascending: false });

      if (error) throw error;

      const transformedItems = data
        .filter((item) => item.market_activity)
        .map((item) => {
          const activity = item.market_activity as any;
          return {
            id: activity.id,
            title: activity.title,
            url: activity.url ?? undefined,
            description: activity.description ?? undefined,
            category: activity.category ?? undefined,
            item_type: activity.item_type ?? undefined,
            published_date: activity.published_date ?? undefined,
            thumbnail_url: activity.thumbnail_url ?? undefined,
            document_url: activity.document_url ?? undefined,
            ai_summary: activity.ai_summary ?? undefined,
          };
        });

      setItems(transformedItems);
    } catch (err) {
      console.error("Error fetching market activity:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item: MarketActivityItem) => {
    const linkUrl = item.url || item.document_url;
    if (linkUrl) {
      window.open(linkUrl, "_blank");
    }
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="blue">
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Market Activity
          </h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Card variant="bordered">
      <CardHeader color="blue">
        <h3 className="text-lg sm:text-xl font-bold text-black">
          Market Activity
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          Soundbites and Noise from Activity across the related markets
        </p>
      </CardHeader>
      <CardContent>
        {/* Fixed height container with internal scroll */}
        <div className="max-h-[600px] overflow-y-auto space-y-3 sm:space-y-4 pr-2">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="border-4 border-black p-3 sm:p-4 bg-gradient-to-br from-blue-100 to-blue-200 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center border-2 border-black">
                  {item.url || item.document_url ? (
                    <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  ) : (
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm sm:text-base font-black text-black uppercase tracking-wide mb-1 group-hover:underline">
                    {item.title}
                  </h4>

                  {item.description && (
                    <p className="text-xs sm:text-sm text-gray-700 mb-2 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 text-xs">
                    {item.category && (
                      <span className="px-2 py-1 bg-blue-500 text-white font-bold border border-black">
                        {item.category}
                      </span>
                    )}
                    {item.item_type && (
                      <span className="px-2 py-1 bg-white text-black font-bold border border-black">
                        {item.item_type}
                      </span>
                    )}
                    {item.published_date && (
                      <span className="px-2 py-1 bg-gray-200 text-black font-bold border border-black">
                        {new Date(item.published_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
